/**
 * Utility functions for FinPay Gateway
 * Following strict design system principles
 */

/**
 * Format currency in Indian Rupee format
 * @param amount - Amount to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, decimals = 0): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/**
 * Format currency without auto-converting to lakhs/crores
 * Shows the exact numerical value
 * @param amount - Amount to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted currency string without rounding
 */
export function formatCurrencyExact(amount: number, decimals = 0): string {
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "decimal",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });

  // Format with thousands separators but without currency symbol
  const formattedNumber = formatter.format(amount);

  // Add ₹ prefix and INR suffix
  return `₹${formattedNumber}`;
}

/**
 * Simple currency formatting with just thousands separators
 * @param amount - Amount to format
 * @returns Formatted currency string
 */
export function formatCurrencySimple(amount: number): string {
  // Convert to string and add thousands separators manually
  const formatted = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `₹${formatted}`;
}

/**
 * Format number with Indian numbering system (lakhs, crores)
 * @param num - Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat("en-IN").format(num);
}

/**
 * Format date to readable format
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(dateObj);
}

/**
 * Format date and time
 * @param date - Date string or Date object
 * @returns Formatted date-time string
 */
export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dateObj);
}

/**
 * Calculate percentage
 * @param value - Current value
 * @param total - Total value
 * @returns Percentage string with 2 decimal places
 */
export function calculatePercentage(value: number, total: number): string {
  if (total === 0) return "0.00";
  return ((value / total) * 100).toFixed(2);
}

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

/**
 * Get status color class for badges
 * @param status - Status string
 * @returns Tailwind color class
 */
export function getStatusColor(
  status: "success" | "pending" | "failed" | "processing"
): string {
  const colors = {
    success: "bg-success/10 text-success border-success/20",
    pending: "bg-warning/10 text-warning border-warning/20",
    failed: "bg-destructive/10 text-destructive border-destructive/20",
    processing: "bg-primary/10 text-primary border-primary/20",
  };
  return colors[status] || colors.pending;
}

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise<boolean> - Success status
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * Generate random transaction ID
 * @returns Transaction ID string
 */
export function generateTransactionId(): string {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, "");
  const random = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `TXN${dateStr}${random}`;
}

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Check if value is within 8px grid system
 * @param value - Value to check (in pixels)
 * @returns boolean - Whether value follows 8px grid
 */
export function isValidSpacing(value: number): boolean {
  const validSpacings = [4, 8, 12, 16, 24, 32, 48, 64, 96, 128];
  return validSpacings.includes(value);
}
